/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1700179200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Nov 17 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "oracle-db",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Oracle DB",
          "depth": 0,
          "id": "connect-to-oracle-db"
        }, {
          "value": "Required permissions",
          "depth": 1,
          "id": "required-permissions"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Column types",
          "depth": 1,
          "id": "column-types"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Batch size",
          "depth": 1,
          "id": "batch-size"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Compatible versions",
          "depth": 1,
          "id": "compatible-versions"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }, {
          "value": "Unsupported data types",
          "depth": 1,
          "id": "unsupported-data-types"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      strong: "strong",
      ul: "ul",
      li: "li",
      em: "em",
      code: "code",
      h3: "h3",
      img: "img"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to an Oracle DB table"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-oracle-db",
        children: "Connect to Oracle DB"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Oracle DB"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". If you're not using a ", _jsx(_components.a, {
          href: "#ssh-tunneling",
          children: "tunnel"
        }), ", you can then authenticate Hightouch to Oracle DB by entering the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your Oracle server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your Oracle server. The default port number is 1521, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": This specifies the database to use when Hightouch executes queries in Oracle DB. This is different than the host, but your host address might contain your database name."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": The user that has access through Hightouch to the database and tables you want to sync to. It's best to ", _jsx(_components.a, {
            href: "https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/CREATE-USER.html",
            children: "create a new user"
          }), " specifically for Hightouch access. ", _jsx(_components.em, {
            children: "Do not use the root user."
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the specified user."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure your ", _jsx(_components.a, {
          href: "#required-permissions",
          children: "credentials"
        }), " are correct, click ", _jsx(_components.strong, {
          children: "Test connection"
        }), ". This confirms if Hightouch can connect to your database by running a basic ", _jsx(_components.code, {
          children: "SELECT"
        }), " query."]
      }), "\n", _jsx(_components.h3, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "The user you use to authenticate must have the following permissions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add, update, and delete (", _jsx(_components.a, {
            href: "#delete-behavior",
            children: "if applicable"
          }), ") rows in your sync's table."]
        }), "\n", _jsxs(_components.li, {
          children: ["Create tables in your database, which are kept only temporarily and used to perform ", _jsx(_components.code, {
            children: "MERGE"
          }), " operations. These tables are cleaned up at the end of each sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["View the following tables which are used for gathering metadata to set up the sync:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "USER_CONSTRAINTS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "USER_CONS_COLUMNS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "USER_CATALOG"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "USER_TAB_COLUMNS"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to Oracle DB over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your Oracle DB instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Oracle DB destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Oracle DB destination you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, with the option to ", _jsx(_components.a, {
          href: "#delete-behavior",
          children: "delete removed rows"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires you select a unique identifier in the table you are syncing to. The model column you select must match a ", _jsx(_components.code, {
          children: "UNIQUE"
        }), ", ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column within Oracle DB."]
      }), "\n", _jsx(_components.p, {
        children: "You can see columns that fit this criteria as available options in records matching section."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-oracle-id-mapping.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If there are no fields in the dropdown, you must add a unique type column to your Oracle DB table. Then, click the refresh icon to access the newly created column."
      }), "\n", _jsx(_components.h3, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch works out of the box with most standard column types, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "VARCHAR2"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "NUMBER"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TIMESTAMP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "DATE"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BINARY_FLOAT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BINARY_DOUBLE"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you see type errors, it may be because your model is producing the wrong format. If so, use ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "typecasting"
        }), " to resolve the issue."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Select which model columns you want to sync to your Oracle DB table columns. Hightouch automatically pulls the columns from your table to make them available for you to map."
      }), "\n", _jsx(_components.h3, {
        id: "batch-size",
        children: "Batch size"
      }), "\n", _jsx(_components.p, {
        children: "You can tune the number of rows that Hightouch upserts per query based on your needs and database threshold. The default is 1000 rows per batch."
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the row in Oracle DB"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete row"
              })
            }), _jsx(_components.td, {
              children: "Remove the row from Oracle DB entirely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "compatible-versions",
        children: "Compatible versions"
      }), "\n", _jsxs(_components.p, {
        children: ["The supported Oracle DB version is ", _jsx(_components.code, {
          children: "19c"
        }), ". Other versions aren't officially supported but may work regardless."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {}), "\n", _jsx(_components.h3, {
        id: "unsupported-data-types",
        children: "Unsupported data types"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch does not support ", _jsx(_components.code, {
          children: "INTERVAL"
        }), " types for Oracle."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
